import React from 'react';
import { Routes, Route, Outlet } from 'react-router-dom';
import { Refine, Authenticated, I18nProvider } from '@refinedev/core';
import { ErrorComponent } from '@refinedev/antd';
import { CatchAllNavigate, DocumentTitleHandler } from '@refinedev/react-router-v6';

// Import pages
import { Dashboard } from './pages/Dashboard';
import { ProcurementPlanList } from './pages/procurement-plan/list';
import { CreateProcurementPlan } from './pages/procurement-plan/create';
import { EditProcurementPlan } from './pages/procurement-plan/edit';
import { ShowProcurementPlan } from './pages/procurement-plan/show';
import { ProcurementPlanReportList } from './pages/procurement-plan-report/list';
import { CreateProcurementPlanReport } from './pages/procurement-plan-report/create';
import { ShowProcurementPlanReport } from './pages/procurement-plan-report/show';
import { UserList } from './pages/users/list';
import { CreateUser } from './pages/users/create';
import { EditUser } from './pages/users/edit';
import { ShowUser } from './pages/users/show';
import { PurchaseRequestList } from './pages/purchase-request/list';
import { CreatePurchaseRequest } from './pages/purchase-request/create'
import { EditPurchaseRequest } from './pages/purchase-request/edit';
import { ShowPurchaseRequest } from './pages/purchase-request/show';
import { PurchaseTemplateList } from './pages/purchase-template/list';
import { CreatePurchaseTemplate } from './pages/purchase-template/create';
import { EditPurchaseTemplate } from './pages/purchase-template/edit';
import { ShowPurchaseTemplate } from './pages/purchase-template/show';

import { PurchaseOrderList } from './pages/purchase-order/list';
import { CreatePurchaseOrder } from './pages/purchase-order/create';
import { EditPurchaseOrder } from './pages/purchase-order/edit';
import { ShowPurchaseOrder } from './pages/purchase-order/show';
import { TeamList } from './pages/teams/list';
import { CreateTeam } from './pages/teams/create';
import { EditTeam } from './pages/teams/edit';
import { ShowTeam } from './pages/teams/show';
import { PermissionList } from './pages/permissions/list';
import { CreatePermission } from './pages/permissions/create';
import { EditPermission } from './pages/permissions/edit';
import { ShowPermission } from './pages/permissions/show';
import { VendorList } from './pages/vendors/list';
import { CreateVendor } from './pages/vendors/create';
import { EditVendor } from './pages/vendors/edit';
import { ShowVendor } from './pages/vendors/show';
import { SourcingList } from './pages/sourcing/list';
import { SourcingCreate } from './pages/sourcing/create';
import { SourcingEdit } from './pages/sourcing/edit';
import { SourcingShow } from './pages/sourcing/show';
import { PublishSourcing } from './pages/sourcing/publish-sourcing/publish';
import { ReceivingList } from './pages/receiving/list';
import { CreateReceiving } from './pages/receiving/create';
import { EditReceiving } from './pages/receiving/edit';
import { ShowReceiving } from './pages/receiving/show';
import { DocumentTemplateList } from './pages/document-template/list';
import { DocumentTemplateCreate } from './pages/document-template/create';
import { DocumentTemplateEdit } from './pages/document-template/edit';
import { DocumentTemplateShow } from './pages/document-template/show';
import { InvoiceList } from './pages/invoice/list';
import { InvoiceEdit } from './pages/invoice/edit';
import { InvoiceShow } from './pages/invoice/show';
import { PaymentRequestList } from './pages/payment-request/list';
import { CreatePaymentRequest } from './pages/payment-request/create';
import { EditPaymentRequest } from './pages/payment-request/edit';
import { ShowPaymentRequest } from './pages/payment-request/show';
import { DefaultCommitteeList } from './pages/default-committee/list';
import { DefaultCommitteeCreate } from './pages/default-committee/create';
import { DefaultCommitteeEdit } from './pages/default-committee/edit';
import { DefaultCommitteeShow } from './pages/default-committee/show';
import { MinuteTemplateList } from './pages/minute-template/list';
import { MinuteTemplateCreate } from './pages/minute-template/create';
import { MinuteTemplateEdit } from './pages/minute-template/edit';
import { MinuteTemplateShow } from './pages/minute-template/show';
import { OrganizationSettings } from './pages/organizations/settings';
import { dataProvider } from './helpers/gqlProvider';
import { authProvider } from './helpers/authProvider';
import { accessControlProvider } from './providers/accessControlProvider';
import { ThemedLayoutV2 } from '@refinedev/antd';
import routerProvider, { UnsavedChangesNotifier } from '@refinedev/react-router-v6';
import { ConfigProvider } from "antd";
import "@refinedev/antd/dist/reset.css";
import { useThemeContext } from './contexts/ThemeContext';
import { Header } from './components/Header';
import { useTranslation } from "react-i18next";

import { BankOutlined, DashboardOutlined, CalendarOutlined, UserOutlined, ShoppingCartOutlined, FileTextOutlined, TeamOutlined, SearchOutlined, BarChartOutlined, SettingOutlined, FolderOutlined, DollarOutlined, CarryOutOutlined, SafetyOutlined } from '@ant-design/icons';
import { ThemedSiderV2 } from "./components/layout/sider";
import { ThemedTitleV2 } from './components/layout/title';


export const App: React.FC = () => {
  const { antdTheme } = useThemeContext();

    const { t, i18n } = useTranslation();

    const i18nProvider: I18nProvider = {
    translate: (key: string, options?: any) => t(key, options) as string,
    changeLocale: (lang: string) => i18n.changeLanguage(lang),
    getLocale: () => i18n.language,
  };
   

  React.useEffect(() => {
    const savedLocale = localStorage.getItem('locale') || 'en';
    i18n.changeLanguage(savedLocale).catch((error) => {
      console.error('Error initializing i18n:', error);
    });
  }, [i18n]);

  return (
    <ConfigProvider theme={antdTheme}>
      <Refine
        dataProvider={dataProvider}
        routerProvider={routerProvider}
        authProvider={authProvider}
        accessControlProvider={accessControlProvider}
        i18nProvider={i18nProvider}
        Dashboard={Dashboard}
        resources={[
          // Dashboard (standalone)
          {
            name: 'dashboard',
            list: '/dashboard',
            icon: <DashboardOutlined />,
          },
          // Procurement Group
          {
            name: 'procurementPlans',
            list: '/procurement-plans',
            create: '/procurement-plans/create',
            edit: '/procurement-plans/edit/:id',
            show: '/procurement-plans/show/:id',
            icon: <CalendarOutlined />,
            meta: { parent: 'procurement' },
          },
          {
            name: 'purchaseRequests',
            list: '/purchase-requests',
            create: '/purchase-requests/create',
            edit: '/purchase-requests/edit/:id',
            show: '/purchase-requests/show/:id',
            icon: <ShoppingCartOutlined />,
            meta: { parent: 'procurement' },
          },
          {
            name: 'procurementPlanReports',
            list: '/procurement-plan-reports',
            create: '/procurement-plan-reports/create',
            show: '/procurement-plan-reports/show/:id',
            icon: <BarChartOutlined />,
            meta: { parent: 'procurement' },
          },
          // Execution Group
          {
            name: 'purchaseOrders',
            list: '/purchase-orders',
            create: '/purchase-orders/create',
            edit: '/purchase-orders/edit/:id',
            show: '/purchase-orders/show/:id',
            icon: <FileTextOutlined />,
            meta: { parent: 'execution' },
          },
          {
            name: 'sourcings',
            list: '/sourcings',
            create: '/sourcings/create',
            edit: '/sourcings/edit/:id',
            show: '/sourcings/show/:id',
            icon: <SearchOutlined />,
            meta: { parent: 'execution' },
          },
          {
            name: 'receivings',
            list: '/receivings',
            create: '/receivings/create',
            edit: '/receivings/edit/:id',
            show: '/receivings/show/:id',
            icon: <CarryOutOutlined />,
            meta: { parent: 'execution' },
          },
          // Templates Group
          {
            name: 'purchaseTemplates',
            list: '/purchase-templates',
            create: '/purchase-templates/create',
            edit: '/purchase-templates/edit/:id',
            show: '/purchase-templates/show/:id',
            icon: <FileTextOutlined />,
            meta: { parent: 'templates' },
          },
          {
            name: 'documentTemplates',
            list: '/document-templates',
            create: '/document-templates/create',
            edit: '/document-templates/edit/:id',
            show: '/document-templates/show/:id',
            icon: <FolderOutlined />,
            meta: { parent: 'templates' },
          },
          {
            name: 'minuteTemplates',
            list: '/minute-templates',
            create: '/minute-templates/create',
            edit: '/minute-templates/edit/:id',
            show: '/minute-templates/show/:id',
            icon: <FileTextOutlined />,
            meta: { parent: 'templates' },
          },
          // Finance Group
          {
            name: 'invoices',
            list: '/invoices',
            edit: '/invoices/edit/:id',
            show: '/invoices/show/:id',
            icon: <FileTextOutlined />,
            meta: { parent: 'finance' },
          },
          {
            name: 'paymentRequests',
            list: '/payment-requests',
            create: '/payment-requests/create',
            edit: '/payment-requests/edit/:id',
            show: '/payment-requests/show/:id',
            icon: <DollarOutlined />,
            meta: { parent: 'finance' },
          },
          // Configuration Group
          {
            name: 'users',
            list: '/users',
            create: '/users/create',
            edit: '/users/edit/:id',
            show: '/users/show/:id',
            icon: <UserOutlined />,
            meta: { parent: 'configuration' },
          },
          {
            name: 'teams',
            list: '/teams',
            create: '/teams/create',
            edit: '/teams/edit/:id',
            show: '/teams/show/:id',
            icon: <TeamOutlined />,
            meta: { parent: 'configuration' },
          },
          {
            name: 'vendors',
            list: '/vendors',
            create: '/vendors/create',
            edit: '/vendors/edit/:id',
            show: '/vendors/show/:id',
            icon: <UserOutlined />,
            meta: { parent: 'configuration' },
          },
          {
            name: 'defaultCommittees',
            list: '/default-committees',
            create: '/default-committees/create',
            edit: '/default-committees/edit/:id',
            show: '/default-committees/show/:id',
            icon: <TeamOutlined />,
            meta: { parent: 'configuration' },
          },
          {
            name: 'permissions',
            list: '/permissions',
            create: '/permissions/create',
            edit: '/permissions/edit/:id',
            show: '/permissions/show/:id',
            icon: <SafetyOutlined />,
            meta: { parent: 'configuration' },
          },
          {
            name: 'organization',
            list: '/organization',
            icon: <BankOutlined />,
            meta: { parent: 'configuration', label: 'Organization' },
          },
          // Menu Group Headers - Refine will automatically create nested menus
          {
            name: 'procurement',
            icon: <CalendarOutlined />,
          },
          {
            name: 'execution',
            icon: <CarryOutOutlined />,
          },
          {
            name: 'templates',
            icon: <FolderOutlined />,
          },
          {
            name: 'finance',
            icon: <DollarOutlined />,
          },
          {
            name: 'configuration',
            icon: <SettingOutlined />,
          },
        ]}
        options={{
          title:{
            text:"TruBuy"
          },
          syncWithLocation: true,
          warnWhenUnsavedChanges: true,
        }}
      >
        <Routes>
          <Route
            element={
              <Authenticated key="protected-routes" fallback={<CatchAllNavigate to="/login" />}>
                <ThemedLayoutV2 Header={Header} Title={ThemedTitleV2} Sider={ThemedSiderV2}>
                  <Outlet />
                </ThemedLayoutV2>
              </Authenticated>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path='dashboard' element={<Dashboard />} />
            <Route path='procurement-plans'>
              <Route index element={<ProcurementPlanList />} />
              <Route path="create" element={<CreateProcurementPlan />} />
              <Route path="edit/:id" element={<EditProcurementPlan />} />
              <Route path="show/:id" element={<ShowProcurementPlan />} />
            </Route>
            <Route path='purchase-requests'>
              <Route index element={<PurchaseRequestList />} />
              <Route path="create" element={<CreatePurchaseRequest />} />
              <Route path="edit/:id" element={<EditPurchaseRequest />} />
              <Route path="show/:id" element={<ShowPurchaseRequest />} />
            </Route>
            <Route path='purchase-templates'>
              <Route index element={<PurchaseTemplateList />} />
              <Route path="create" element={<CreatePurchaseTemplate />} />
              <Route path="edit/:id" element={<EditPurchaseTemplate />} />
              <Route path="show/:id" element={<ShowPurchaseTemplate />} />
            </Route>
            <Route path='procurement-plan-reports'>
              <Route index element={<ProcurementPlanReportList />} />
              <Route path="create" element={<CreateProcurementPlanReport />} />
              <Route path="show/:id" element={<ShowProcurementPlanReport />} />
            </Route>
            <Route path='users'>
              <Route index element={<UserList />} />
              <Route path="create" element={<CreateUser />} />
              <Route path="edit/:id" element={<EditUser />} />
              <Route path="show/:id" element={<ShowUser />} />
            </Route>
            <Route path='teams'>
              <Route index element={<TeamList />} />
              <Route path="create" element={<CreateTeam />} />
              <Route path="edit/:id" element={<EditTeam />} />
              <Route path="show/:id" element={<ShowTeam />} />
            </Route>
            <Route path='vendors'>
              <Route index element={<VendorList />} />
              <Route path="create" element={<CreateVendor />} />
              <Route path="edit/:id" element={<EditVendor />} />
              <Route path="show/:id" element={<ShowVendor />} />
            </Route>
            <Route path='purchase-orders'>
              <Route index element={<PurchaseOrderList />} />
              <Route path="create" element={<CreatePurchaseOrder />} />
              <Route path="edit/:id" element={<EditPurchaseOrder />} />
              <Route path="show/:id" element={<ShowPurchaseOrder />} />
            </Route>
            <Route path='sourcings'>
              <Route index element={<SourcingList />} />
              <Route path="create" element={<SourcingCreate />} />
              <Route path="edit/:id" element={<SourcingEdit />} />
              <Route path="show/:id" element={<SourcingShow />} />
              <Route path="publish/:id" element={<PublishSourcing />} />
            </Route>
            <Route path='receivings'>
              <Route index element={<ReceivingList />} />
              <Route path="create" element={<CreateReceiving />} />
              <Route path="edit/:id" element={<EditReceiving />} />
              <Route path="show/:id" element={<ShowReceiving />} />
            </Route>
            <Route path='document-templates'>
              <Route index element={<DocumentTemplateList />} />
              <Route path="create" element={<DocumentTemplateCreate />} />
              <Route path="edit/:id" element={<DocumentTemplateEdit />} />
              <Route path="show/:id" element={<DocumentTemplateShow />} />
            </Route>
            <Route path='invoices'>
              <Route index element={<InvoiceList />} />
              <Route path="edit/:id" element={<InvoiceEdit />} />
              <Route path="show/:id" element={<InvoiceShow />} />
            </Route>
            <Route path='payment-requests'>
              <Route index element={<PaymentRequestList />} />
              <Route path="create" element={<CreatePaymentRequest />} />
              <Route path="edit/:id" element={<EditPaymentRequest />} />
              <Route path="show/:id" element={<ShowPaymentRequest />} />
            </Route>
            <Route path='default-committees'>
              <Route index element={<DefaultCommitteeList />} />
              <Route path="create" element={<DefaultCommitteeCreate />} />
              <Route path="edit/:id" element={<DefaultCommitteeEdit />} />
              <Route path="show/:id" element={<DefaultCommitteeShow />} />
            </Route>
            <Route path='permissions'>
              <Route index element={<PermissionList />} />
              <Route path="create" element={<CreatePermission />} />
              <Route path="edit/:id" element={<EditPermission />} />
              <Route path="show/:id" element={<ShowPermission />} />
            </Route>
            <Route path='minute-templates'>
              <Route index element={<MinuteTemplateList />} />
              <Route path="create" element={<MinuteTemplateCreate />} />
              <Route path="edit/:id" element={<MinuteTemplateEdit />} />
              <Route path="show/:id" element={<MinuteTemplateShow />} />
            </Route>
            <Route path='organization' element={<OrganizationSettings />} />
          </Route>
          <Route
            element={
              <Authenticated key="auth-pages" fallback={<>Redirecting to login...</>}>
                <CatchAllNavigate to="/" />
              </Authenticated>
            }
          >
            <Route path="/login" element={
              <div>
                Redirecting to authentication service...
              </div>
            } />
          </Route>
          <Route path="*" element={<ErrorComponent />} />
        </Routes>
        <UnsavedChangesNotifier />
        <DocumentTitleHandler />
      </Refine>
    </ConfigProvider>
  );
};
